import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import type { ColumnDef } from '@quartup/reports-shared';
import {
  type SortingState,
  type VisibilityState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import type React from 'react';
import { useRef, useState } from 'react';
import { ResultsEmptyState } from './results-empty-state.js';
import { ResultsTableCell } from './results-table-cell.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ResultsTableProps {
  columns: ColumnDef[];
  rows: ReadonlyArray<Record<string, unknown>>;
  totalRows: number;
  locale?: string;
}

const ROW_HEIGHT = 40;
const OVERSCAN = 10;

// ---------------------------------------------------------------------------
// Column settings popover
// ---------------------------------------------------------------------------

function ColumnSettings({
  columns,
  visibility,
  onToggle,
}: {
  columns: ColumnDef[];
  visibility: VisibilityState;
  onToggle: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-[var(--radius-button)] border border-[var(--color-qup-border-dark)] px-3 py-1.5 text-xs font-medium',
          'text-[var(--color-qup-text-muted-dark)] hover:bg-[var(--color-qup-bg-dark)] transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
        )}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
          <path
            fillRule="evenodd"
            d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
            clipRule="evenodd"
          />
        </svg>
        {t('report.column_settings')}
      </button>

      {open && (
        <>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss; not keyboard-navigable by design */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            className={cn(
              'absolute right-0 z-50 mt-1 w-52 rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
              'bg-[var(--color-qup-bg-card-dark)] p-2 shadow-[var(--shadow-card-hover)]',
            )}
          >
            <p className="mb-2 px-1 text-xs font-semibold text-[var(--color-qup-text-muted-dark)] uppercase tracking-wide">
              {t('report.column_settings')}
            </p>
            <ul className="space-y-0.5">
              {columns.map((col) => {
                const visible = visibility[col.key] !== false;
                const label = col.label?.es ?? col.key;
                return (
                  <li key={col.key}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors duration-100',
                        'hover:bg-[var(--color-qup-bg-dark)] text-[var(--color-qup-text-dark)]',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={visible}
                        onChange={() => onToggle(col.key)}
                        className="h-3.5 w-3.5 accent-[var(--color-qup-primary)]"
                      />
                      {label}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main table
// ---------------------------------------------------------------------------

// MVP: only 'es' locale is supported for column labels
function getColumnLabel(
  label: { es: string; en?: string | undefined; ca?: string | undefined } | undefined,
  _locale: string,
): string {
  return label?.es ?? '';
}

export function ResultsTable({
  columns: colDefs,
  rows,
  totalRows,
  locale = 'es',
}: ResultsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const parentRef = useRef<HTMLDivElement>(null);

  const helper = createColumnHelper<Record<string, unknown>>();

  const tableColumns = colDefs.map((col) =>
    helper.accessor((row) => row[col.key], {
      id: col.key,
      header: getColumnLabel(col.label, locale) || col.key,
      enableSorting: col.sortable !== false,
      cell: (info) => (
        <ResultsTableCell
          value={info.getValue()}
          type={col.type}
          currency={col.currency}
          decimals={col.decimals}
        />
      ),
    }),
  );

  const table = useReactTable({
    data: rows as Record<string, unknown>[],
    columns: tableColumns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { rows: tableRows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  function interpolate(template: string, values: Record<string, string | number>): string {
    return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
  }

  function handleToggleColumn(key: string) {
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  }

  if (rows.length === 0) return <ResultsEmptyState />;

  return (
    <div className="flex flex-col gap-2">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-1">
        <p className="text-xs text-[var(--color-qup-text-muted-dark)]">
          {interpolate(t('report.showing_rows'), { shown: tableRows.length, total: totalRows })}
        </p>
        <ColumnSettings
          columns={colDefs}
          visibility={columnVisibility}
          onToggle={handleToggleColumn}
        />
      </div>

      {/* Scrollable table container */}
      <div
        ref={parentRef}
        className="overflow-auto rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]"
        style={{ maxHeight: 560 }}
      >
        {/* biome-ignore lint/a11y/useSemanticElements: role="grid" needed for TanStack Table keyboard nav */}
        <table className="w-full border-collapse text-sm" role="grid" aria-rowcount={totalRows}>
          {/* Sticky header */}
          <thead className="sticky top-0 z-10 bg-[var(--color-qup-bg-dark)]">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-[var(--color-qup-border-dark)]">
                {headerGroup.headers.map((header) => {
                  const isSorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className={cn(
                        'whitespace-nowrap px-3 py-2.5 text-left text-xs font-semibold text-[var(--color-qup-text-muted-dark)] uppercase tracking-wide',
                        canSort &&
                          'cursor-pointer select-none hover:text-[var(--color-qup-text-dark)]',
                      )}
                      aria-sort={
                        isSorted === 'asc'
                          ? 'ascending'
                          : isSorted === 'desc'
                            ? 'descending'
                            : undefined
                      }
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      onKeyDown={
                        canSort
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                header.column.getToggleSortingHandler()?.(
                                  e as unknown as React.MouseEvent,
                                );
                              }
                            }
                          : undefined
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span
                            aria-label={
                              isSorted === 'asc'
                                ? 'Ordenado ascendente'
                                : isSorted === 'desc'
                                  ? 'Ordenado descendente'
                                  : 'Sin ordenar'
                            }
                          >
                            {isSorted === 'asc' ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 12 12"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path d="M6 2l4 6H2z" />
                              </svg>
                            ) : isSorted === 'desc' ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 12 12"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path d="M6 10L2 4h8z" />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3 opacity-30"
                                viewBox="0 0 12 12"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path d="M6 2l4 6H2zm0 8L2 4h8z" />
                              </svg>
                            )}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          {/* Virtualized body */}
          <tbody style={{ height: `${totalSize}px`, position: 'relative' }} aria-busy={false}>
            {virtualItems.map((virtualRow) => {
              const row = tableRows[virtualRow.index];
              if (row === undefined) return null;
              return (
                <tr
                  key={row.id}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  className={cn(
                    'absolute w-full border-b border-[var(--color-qup-border-dark)]/50',
                    'transition-colors duration-100',
                    virtualRow.index % 2 === 0
                      ? 'bg-[var(--color-qup-bg-card-dark)]'
                      : 'bg-[var(--color-qup-bg-dark)]',
                    'hover:bg-[var(--color-qup-primary)]/5',
                  )}
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2 text-[var(--color-qup-text-dark)]">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-1 text-right text-xs text-[var(--color-qup-text-muted-dark)]">
        {interpolate(t('report.showing_rows'), { shown: tableRows.length, total: totalRows })}
      </div>
    </div>
  );
}
